import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const FILTERS = ['open', 'partial', 'paid', 'void', 'all'];
const emptyBill = () => ({
  vendor: '', bill_number: '', bill_date: today(), due_date: '',
  expense_account_id: '', amount: '', tax_amount: '', notes: '', receipt_file_url: '', ocr_raw: null,
});

export default function Bills() {
  const { activeId } = useBusiness();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('open');
  const [summary, setSummary] = useState({});
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyBill());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState(null);

  const [pay, setPay] = useState(null); // payment modal state

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/bills?business_id=${activeId}&status=${filter}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load bills.'); })
      .finally(() => setLoading(false));
  }, [activeId, filter]);

  const loadSummary = useCallback(() => {
    api(`/bills/summary?business_id=${activeId}`).then(setSummary).catch(() => setSummary({}));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary, rows]);
  useEffect(() => {
    api(`/accounts?business_id=${activeId}&type=expense`).then((d) => setExpenseAccounts(Array.isArray(d) ? d : [])).catch(() => setExpenseAccounts([]));
    api(`/bills/bank-accounts?business_id=${activeId}`).then((d) => setBankAccounts(Array.isArray(d) ? d : [])).catch(() => setBankAccounts([]));
  }, [activeId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = (Number(form.amount) || 0) + (Number(form.tax_amount) || 0);

  function openNew() {
    setCurrent(null); setForm(emptyBill()); setFormError(null); setScanNote(null); setOpen(true);
  }
  async function openRow(row) {
    setFormError(null); setScanNote(null);
    try {
      const b = await api(`/bills/${row.id}`);
      setCurrent(b);
      setForm({
        vendor: b.vendor || '', bill_number: b.bill_number || '',
        bill_date: b.bill_date?.slice(0, 10) || '', due_date: b.due_date?.slice(0, 10) || '',
        expense_account_id: b.expense_account_id || '', amount: b.amount ?? '', tax_amount: b.tax_amount ?? '',
        notes: b.notes || '', receipt_file_url: b.receipt_file_url || '', ocr_raw: null,
      });
      setOpen(true);
    } catch { setFormError('Could not open that bill.'); }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setFormError('Invoice file must be under 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => { set('receipt_file_url', String(reader.result)); setScanNote(null); };
    reader.readAsDataURL(file);
  }

  async function scanInvoice() {
    if (!form.receipt_file_url) return;
    setScanning(true); setScanNote(null); setFormError(null);
    try {
      const r = await api('/bills/scan', { method: 'POST', body: { business_id: activeId, file: form.receipt_file_url } });
      setForm((f) => ({
        ...f,
        vendor: r.vendor || f.vendor,
        bill_date: r.bill_date || f.bill_date,
        amount: r.amount != null ? r.amount : f.amount,
        tax_amount: r.tax_amount != null ? r.tax_amount : f.tax_amount,
        ocr_raw: r.ocr_raw || null,
      }));
      setScanNote(r.amount != null ? 'Read the invoice — review the fields, pick a category, then save.' : 'Couldn\u2019t read an amount — enter the details manually.');
    } catch (e) { setFormError(e?.detail || 'Could not scan the invoice.'); }
    finally { setScanning(false); }
  }

  async function save() {
    if (!form.vendor.trim()) { setFormError('Vendor is required.'); return; }
    if (!form.expense_account_id) { setFormError('Choose an expense category.'); return; }
    if (total <= 0) { setFormError('Enter an amount greater than zero.'); return; }
    setSaving(true); setFormError(null);
    const body = {
      business_id: activeId, vendor: form.vendor.trim(), bill_number: form.bill_number || null,
      bill_date: form.bill_date || null, due_date: form.due_date || null,
      expense_account_id: form.expense_account_id, amount: Number(form.amount) || 0,
      tax_amount: Number(form.tax_amount) || 0, notes: form.notes || null,
      receipt_file_url: form.receipt_file_url || null, ocr_raw: form.ocr_raw || undefined,
    };
    try {
      if (current) await api(`/bills/${current.id}`, { method: 'PUT', body });
      else await api('/bills', { method: 'POST', body });
      setOpen(false); load();
    } catch (e) { setFormError(e?.detail || 'Could not save the bill.'); }
    finally { setSaving(false); }
  }

  async function voidBill() {
    if (!current) return;
    if (!window.confirm('Void this bill? This removes its accrual from the ledger.')) return;
    try { await api(`/bills/${current.id}/void`, { method: 'POST' }); setOpen(false); load(); }
    catch (e) { setFormError(e?.detail || 'Could not void the bill.'); }
  }
  async function deleteBill() {
    if (!current) return;
    if (!window.confirm('Delete this bill permanently?')) return;
    try { await api(`/bills/${current.id}`, { method: 'DELETE' }); setOpen(false); load(); }
    catch (e) { setFormError(e?.detail || 'Could not delete the bill.'); }
  }

  function openPay() {
    setPay({ amount: String(current.balance ?? ''), bank_account_id: bankAccounts[0]?.id || '',
      payment_date: today(), method: '', reference: '', saving: false, error: null });
  }
  async function submitPay() {
    if (!pay.bank_account_id) { setPay((p) => ({ ...p, error: 'Choose the bank account.' })); return; }
    if (!(Number(pay.amount) > 0)) { setPay((p) => ({ ...p, error: 'Enter a payment amount.' })); return; }
    setPay((p) => ({ ...p, saving: true, error: null }));
    try {
      const updated = await api(`/bills/${current.id}/pay`, { method: 'POST', body: {
        amount: Number(pay.amount), bank_account_id: Number(pay.bank_account_id),
        payment_date: pay.payment_date, method: pay.method || null, reference: pay.reference || null,
      } });
      setCurrent(updated); setPay(null); load();
    } catch (e) { setPay((p) => ({ ...p, saving: false, error: e?.detail || 'Could not record the payment.' })); }
  }
  async function reversePayment(pid) {
    if (!window.confirm('Reverse this payment?')) return;
    try { const updated = await api(`/bills/${current.id}/payments/${pid}`, { method: 'DELETE' }); setCurrent(updated); load(); }
    catch (e) { setFormError(e?.detail || 'Could not reverse the payment.'); }
  }

  const columns = [
    { key: 'vendor', header: 'Vendor', render: (r) => <span>{r.vendor}{r.bill_number ? <span className="hint"> #{r.bill_number}</span> : ''}</span> },
    { key: 'due_date', header: 'Due', render: (r) => {
      if (!r.due_date) return '—';
      const overdue = ['open', 'partial'].includes(r.status) && r.due_date.slice(0, 10) < today();
      return <span className={overdue ? 'due-over' : ''}>{date(r.due_date)}</span>;
    } },
    { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
    { key: 'balance', header: 'Balance', render: (r) => <span className="num">{money(r.balance)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const readOnly = current && (current.status === 'paid' || current.status === 'void' || Number(current.amount_paid) > 0);

  return (
    <>
      <PageHeader title="Bills" actions={<button className="btn btn--primary" onClick={openNew}>New bill</button>} />

      <div className="bills-summary">
        <div className="stat"><div className="stat__label">Open bills</div><div className="stat__value">{summary.open_count || 0}</div></div>
        <div className="stat"><div className="stat__label">Owed</div><div className="stat__value">{money(summary.owed || 0)}</div></div>
        <div className="stat"><div className="stat__label">Overdue</div><div className={'stat__value' + (Number(summary.overdue) > 0 ? ' stat__value--alert' : '')}>{money(summary.overdue || 0)}</div></div>
      </div>

      <div className="filter-chips">
        {FILTERS.map((f) => (
          <button key={f} className={'chip' + (filter === f ? ' chip--on' : '')} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow}
          empty="No bills here. Use New bill to scan or enter a vendor invoice to pay." />
      </section>

      <Drawer
        open={open}
        title={current ? `Bill — ${current.vendor}` : 'New bill'}
        onClose={() => !saving && setOpen(false)}
        footer={
          current ? (
            <>
              {current.status !== 'void' && Number(current.balance) > 0 && <button className="btn btn--primary" onClick={openPay}>Record payment</button>}
              {!readOnly && <button className="btn btn--danger" onClick={voidBill}>Void</button>}
              {!readOnly && <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>}
              <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Close</button>
            </>
          ) : (
            <>
              <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save bill'}</button>
            </>
          )
        }
      >
        {formError && <div className="form-error">{formError}</div>}
        {current && (
          <div className="inv-status-row">
            <StatusBadge status={current.status} />
            <span className="hint">Total {money(current.total)} · Paid {money(current.amount_paid)} · Balance {money(current.balance)}</span>
          </div>
        )}

        {!readOnly && (
          <div className="field">
            <label>Scan an invoice (optional)</label>
            <input type="file" accept="image/*,application/pdf" onChange={onFile} />
            {form.receipt_file_url && (
              <button type="button" className="btn btn--sm add-acct" onClick={scanInvoice} disabled={scanning}>{scanning ? 'Reading…' : 'Read invoice with OCR'}</button>
            )}
            {scanNote && <p className="form-note" style={{ marginTop: 6 }}>{scanNote}</p>}
          </div>
        )}

        <div className="form-grid">
          <div className="field"><label>Vendor</label>
            <input value={form.vendor} disabled={readOnly} onChange={(e) => set('vendor', e.target.value)} /></div>
          <div className="field"><label>Bill #</label>
            <input value={form.bill_number} disabled={readOnly} onChange={(e) => set('bill_number', e.target.value)} /></div>
        </div>
        <div className="form-grid">
          <div className="field"><label>Bill date</label>
            <input type="date" value={form.bill_date} disabled={readOnly} onChange={(e) => set('bill_date', e.target.value)} /></div>
          <div className="field"><label>Due date</label>
            <input type="date" value={form.due_date} disabled={readOnly} onChange={(e) => set('due_date', e.target.value)} /></div>
        </div>
        <div className="field"><label>Expense category</label>
          <select value={form.expense_account_id} disabled={readOnly} onChange={(e) => set('expense_account_id', e.target.value)}>
            <option value="">Select…</option>
            {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
          </select>
        </div>
        <div className="form-grid">
          <div className="field"><label>Amount (pre-tax)</label>
            <input type="number" step="0.01" value={form.amount} disabled={readOnly} onChange={(e) => set('amount', e.target.value)} /></div>
          <div className="field"><label>Tax</label>
            <input type="number" step="0.01" value={form.tax_amount} disabled={readOnly} onChange={(e) => set('tax_amount', e.target.value)} /></div>
          <div className="field"><label>Total</label>
            <input value={money(total)} disabled readOnly /></div>
        </div>
        <div className="field"><label>Notes</label>
          <textarea rows={2} value={form.notes} disabled={readOnly} onChange={(e) => set('notes', e.target.value)} /></div>

        {current?.receipt_file_url && (
          <div className="field"><label>Invoice</label>
            <a className="btn btn--sm" href={current.receipt_file_url} target="_blank" rel="noreferrer">Open scanned invoice</a>
          </div>
        )}

        {current?.payments?.length > 0 && (
          <div className="field">
            <label>Payments</label>
            <div className="runs">
              {current.payments.map((p) => (
                <div className="runs__row" key={p.id}>
                  <span>{date(p.payment_date)}</span>
                  <span>{p.bank_name || '—'}{p.reference ? ` · ${p.reference}` : ''}</span>
                  <span className="num">{money(p.amount)}</span>
                  {current.status !== 'void' && <button className="btn btn--sm btn--danger" onClick={() => reversePayment(p.id)}>Reverse</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {current && current.status === 'void' && <p className="hint">This bill is void. Its ledger accrual has been removed.</p>}
        {current && !readOnly && <p className="hint">Saving records this as owed (Accounts Payable). You can record a payment after saving.</p>}
        {readOnly && current?.status !== 'void' && current?.amount_paid > 0 && <p className="hint">This bill has payments, so its amount is locked. Reverse the payments to edit it.</p>}
        {current && !readOnly && <div style={{ marginTop: 12 }}><button className="btn btn--sm btn--danger" onClick={deleteBill}>Delete bill</button></div>}
      </Drawer>

      <Modal
        open={!!pay}
        title="Record bill payment"
        onClose={() => { if (!pay?.saving) setPay(null); }}
        footer={<>
          <button className="btn" onClick={() => setPay(null)} disabled={pay?.saving}>Cancel</button>
          <button className="btn btn--primary" onClick={submitPay} disabled={pay?.saving}>{pay?.saving ? 'Saving…' : 'Record payment'}</button>
        </>}
      >
        {pay && (
          <>
            {pay.error && <div className="form-error">{pay.error}</div>}
            <div className="form-grid">
              <div className="field"><label>Amount</label>
                <input type="number" step="0.01" autoFocus value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
              <div className="field"><label>Date</label>
                <input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></div>
            </div>
            <div className="field"><label>Pay from (bank account)</label>
              <select value={pay.bank_account_id} onChange={(e) => setPay({ ...pay, bank_account_id: e.target.value })}>
                <option value="">Select…</option>
                {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
            </div>
            <div className="form-grid">
              <div className="field"><label>Method</label>
                <input value={pay.method} placeholder="ACH, check, card…" onChange={(e) => setPay({ ...pay, method: e.target.value })} /></div>
              <div className="field"><label>Reference</label>
                <input value={pay.reference} placeholder="Check # / confirmation" onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
            </div>
            <p className="hint">Records Dr Accounts Payable / Cr the bank account, and updates the bill balance.</p>
          </>
        )}
      </Modal>
    </>
  );
}
